import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Logo,
  ArrowUpRightIcon,
  PlayIcon,
  SparkleIcon,
  CalendarIcon,
  FileIcon,
  BookIcon,
  ShieldIcon,
  LayersIcon,
  MenuIcon,
  CloseIcon,
  CheckIcon,
} from '../UI/Icons';

const navLinks = [
  { label: 'Home', href: '#home' },
  { label: 'Features', href: '#features' },
  { label: 'How it works', href: '#how' },
  { label: 'FAQ', href: '#faq' },
];

const features = [
  {
    icon: SparkleIcon,
    title: 'AI advisor that knows your record',
    body: 'Ask anything about your degree. Answers are grounded in your transcript, your schedule, and the live course catalog — not generic advice.',
  },
  {
    icon: CalendarIcon,
    title: 'Plan every term to 4B',
    body: 'Lay out study terms and co-op work terms side by side, label them 1A through 4B, and compare alternate paths as separate plans.',
  },
  {
    icon: FileIcon,
    title: 'Transcript in, plan out',
    body: 'Upload your official PDF transcript. We parse courses, grades, and term averages, then generate a plan for the terms you have left.',
  },
  {
    icon: BookIcon,
    title: 'Live Waterloo course data',
    body: 'Search the real catalog straight from UW Open Data — descriptions, prerequisites, and which courses actually run this term.',
  },
  {
    icon: LayersIcon,
    title: 'Multiple plans, no guesswork',
    body: 'Keep a co-op stream, a minor, and a lighter-load option side by side. Switch between them without losing any work.',
  },
  {
    icon: ShieldIcon,
    title: 'Your data stays yours',
    body: 'Transcripts are tied to your account alone, never shared between students, and removable the moment you want them gone.',
  },
];

const steps = [
  {
    step: '01',
    title: 'Upload your transcript',
    body: 'Drop in the PDF from Quest. We pull out every course, grade, and term average automatically.',
  },
  {
    step: '02',
    title: 'Generate your plan',
    body: 'Pathfinder maps out the terms between where you are now and 4B, including co-op work terms.',
  },
  {
    step: '03',
    title: 'Ask, adjust, repeat',
    body: 'Fill terms from the live catalog and ask the advisor what fits — it already knows what you have taken.',
  },
];

const faqs = [
  {
    q: 'Which transcript should I upload?',
    a: 'The unofficial transcript PDF from Quest works best. We read the course codes, grades, term averages, and your program details straight from the file.',
  },
  {
    q: 'Does it know about co-op work terms?',
    a: 'Yes. Terms can be labelled 1A through 4B or WT1 through WT6, and you can still add courses to a work term if you are taking one on the side.',
  },
  {
    q: 'Where does the course data come from?',
    a: 'Directly from the University of Waterloo Open Data API, so descriptions, prerequisites, and term availability reflect the real catalog.',
  },
  {
    q: 'Is UW Pathfinder affiliated with the University of Waterloo?',
    a: 'No. It is an independent student project that reads public course data and the transcript you choose to upload.',
  },
];

const Landing: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div className="min-h-screen bg-[#E9E9EE] px-3 py-3 sm:px-6 sm:py-6">
      <div className="mx-auto max-w-[1600px] overflow-hidden rounded-3xl bg-white shadow-shell sm:rounded-[2.5rem]">
        {/* ---------- Hero ---------- */}
        <section id="home" className="mesh-gradient relative">
          {/* Nav */}
          <header className="relative z-20 flex items-center justify-between px-5 py-5 sm:px-10 sm:py-7">
            <Link to="/" className="flex items-center gap-3">
              <Logo size={44} />
              <span className="font-display text-xl font-semibold tracking-tight text-canvas-900 sm:text-2xl">
                UW Pathfinder
              </span>
            </Link>

            {/* Pill nav */}
            <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-1 rounded-full bg-white/80 p-1.5 shadow-pill backdrop-blur-md lg:flex">
              {navLinks.map((link, i) => (
                <a
                  key={link.label}
                  href={link.href}
                  className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                    i === 0
                      ? 'bg-ink text-white'
                      : 'text-canvas-700 hover:bg-canvas-100 hover:text-canvas-900'
                  }`}
                >
                  {link.label}
                </a>
              ))}
            </nav>

            <div className="hidden items-center gap-1 rounded-full bg-white/80 p-1.5 shadow-pill backdrop-blur-md sm:flex">
              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  className="rounded-full bg-ink px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-ink-600"
                >
                  Go to dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="rounded-full px-5 py-2 text-sm font-medium text-canvas-700 transition-colors hover:bg-canvas-100 hover:text-canvas-900"
                  >
                    Log in
                  </Link>
                  <Link
                    to="/register"
                    className="rounded-full bg-ink px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-ink-600"
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="rounded-full bg-white/80 p-2.5 text-canvas-800 shadow-pill backdrop-blur-md sm:hidden"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <CloseIcon /> : <MenuIcon />}
            </button>
          </header>

          {/* Mobile menu */}
          {mobileOpen && (
            <div className="relative z-20 mx-5 mb-2 animate-fade-in rounded-2xl bg-white/90 p-3 shadow-pill backdrop-blur-md sm:hidden">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="block rounded-xl px-4 py-2.5 text-sm font-medium text-canvas-700 hover:bg-canvas-100"
                >
                  {link.label}
                </a>
              ))}
              <div className="mt-2 flex gap-2 border-t border-canvas-200 pt-3">
                <Link to="/login" className="btn-secondary flex-1">
                  Log in
                </Link>
                <Link to="/register" className="btn-primary flex-1">
                  Sign Up
                </Link>
              </div>
            </div>
          )}

          {/* Hero copy */}
          <div className="relative z-10 px-5 pb-24 pt-10 text-center sm:px-10 sm:pb-32 sm:pt-16">
            <div className="animate-fade-up">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/60 bg-white/70 px-4 py-2 text-xs font-medium text-canvas-700 shadow-pill backdrop-blur-md sm:text-sm">
                <SparkleIcon width={15} height={15} className="text-primary-600" />
                AI Academic Planning for Waterloo
              </span>
            </div>

            <h1
              className="mx-auto mt-8 max-w-5xl animate-fade-up font-display text-[2.5rem] font-bold leading-[1.05] tracking-[-0.03em] text-canvas-800 sm:text-6xl lg:text-7xl"
              style={{ animationDelay: '80ms' }}
            >
              &ldquo;Plan Your Degree with Real Academic Insight&rdquo;
            </h1>

            <p
              className="mx-auto mt-7 max-w-2xl animate-fade-up text-base leading-relaxed text-canvas-600 sm:text-lg"
              style={{ animationDelay: '160ms' }}
            >
              UW Pathfinder turns your transcript into a plan — track every term, search the live
              course catalog, and get advice that actually knows what you have taken.
            </p>

            <div
              className="mt-10 flex animate-fade-up flex-col items-center justify-center gap-3 sm:flex-row"
              style={{ animationDelay: '240ms' }}
            >
              <Link
                to={isAuthenticated ? '/dashboard' : '/register'}
                className="btn-primary w-full px-7 py-3.5 text-base sm:w-auto"
              >
                {isAuthenticated ? 'Go to dashboard' : 'Get Started Free'}
                <ArrowUpRightIcon width={17} height={17} />
              </Link>
              <Link
                to={isAuthenticated ? '/advisor' : '/login'}
                className="btn-secondary w-full px-7 py-3.5 text-base sm:w-auto"
              >
                <PlayIcon width={13} height={13} />
                {isAuthenticated ? 'Ask the advisor' : 'I have an account'}
              </Link>
            </div>

            <div
              className="mx-auto mt-14 flex max-w-2xl animate-fade-up flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-canvas-600"
              style={{ animationDelay: '320ms' }}
            >
              {['Free to use', 'No credit card', 'Live UW course data'].map((item) => (
                <span key={item} className="inline-flex items-center gap-2">
                  <CheckIcon width={15} height={15} className="text-primary-600" />
                  {item}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ---------- Features ---------- */}
        <section id="features" className="px-5 py-20 sm:px-10 sm:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <span className="chip bg-primary-50 text-primary-700">Features</span>
            <h2 className="mt-5 font-display text-3xl font-bold tracking-[-0.02em] text-canvas-900 sm:text-5xl">
              Everything your degree plan needs
            </h2>
            <p className="mt-4 text-base text-canvas-600 sm:text-lg">
              Built around the way Waterloo actually works — study terms, work terms, and a catalog
              that changes every four months.
            </p>
          </div>

          <div className="mx-auto mt-14 grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {features.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="group card p-7 transition-all hover:-translate-y-1 hover:shadow-card-hover"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50 text-primary-700 transition-colors group-hover:bg-ink group-hover:text-white">
                  <Icon width={22} height={22} />
                </span>
                <h3 className="mt-5 font-display text-lg font-semibold text-canvas-900">{title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-canvas-600">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ---------- How it works ---------- */}
        <section id="how" className="mesh-gradient-soft px-5 py-20 sm:px-10 sm:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <span className="chip bg-white/80 text-canvas-700 backdrop-blur">How it works</span>
            <h2 className="mt-5 font-display text-3xl font-bold tracking-[-0.02em] text-canvas-900 sm:text-5xl">
              Three steps to a full plan
            </h2>
          </div>

          <div className="mx-auto mt-14 grid max-w-5xl gap-5 md:grid-cols-3">
            {steps.map(({ step, title, body }) => (
              <div key={step} className="card relative overflow-hidden p-7">
                <span className="font-display text-5xl font-bold text-primary-100">{step}</span>
                <h3 className="mt-3 font-display text-lg font-semibold text-canvas-900">{title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-canvas-600">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ---------- FAQ ---------- */}
        <section id="faq" className="px-5 py-20 sm:px-10 sm:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <span className="chip bg-primary-50 text-primary-700">FAQ</span>
            <h2 className="mt-5 font-display text-3xl font-bold tracking-[-0.02em] text-canvas-900 sm:text-5xl">
              Questions, answered
            </h2>
          </div>

          <div className="mx-auto mt-12 max-w-3xl space-y-3">
            {faqs.map((faq, i) => (
              <div key={faq.q} className="card overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                >
                  <span className="font-medium text-canvas-900">{faq.q}</span>
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-canvas-100 text-canvas-700 transition-transform ${
                      openFaq === i ? 'rotate-45' : ''
                    }`}
                  >
                    <PlusIconSmall />
                  </span>
                </button>
                {openFaq === i && (
                  <p className="animate-fade-in px-6 pb-5 text-sm leading-relaxed text-canvas-600">
                    {faq.a}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* ---------- CTA ---------- */}
        <section className="px-5 pb-20 sm:px-10 sm:pb-28">
          <div className="relative mx-auto max-w-6xl overflow-hidden rounded-3xl bg-ink px-8 py-16 text-center sm:px-16 sm:py-20">
            <div
              className="pointer-events-none absolute inset-0 opacity-70"
              style={{
                backgroundImage:
                  'radial-gradient(at 20% 20%, rgba(167,139,250,0.35) 0px, transparent 50%), radial-gradient(at 85% 75%, rgba(255,184,132,0.25) 0px, transparent 50%)',
              }}
            />
            <div className="relative">
              <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold leading-tight tracking-[-0.02em] text-white sm:text-5xl">
                Stop guessing what to take next
              </h2>
              <p className="mx-auto mt-5 max-w-xl text-base text-white/70 sm:text-lg">
                Upload a transcript and have a term-by-term plan in a couple of minutes.
              </p>
              <Link
                to="/register"
                className="mt-9 inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-semibold text-ink shadow-btn transition-all hover:-translate-y-0.5"
              >
                Create your free account
                <ArrowUpRightIcon width={17} height={17} />
              </Link>
            </div>
          </div>
        </section>

        {/* ---------- Footer ---------- */}
        <footer className="border-t border-canvas-200 px-5 py-10 sm:px-10">
          <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-5 sm:flex-row">
            <div className="flex items-center gap-3">
              <Logo size={34} />
              <span className="font-display text-base font-semibold text-canvas-900">
                UW Pathfinder
              </span>
            </div>
            <p className="text-center text-sm text-canvas-500">
              An independent student project. Not affiliated with the University of Waterloo.
            </p>
            <div className="flex items-center gap-5 text-sm text-canvas-600">
              <Link to="/login" className="transition-colors hover:text-canvas-900">
                Log in
              </Link>
              <Link to="/register" className="transition-colors hover:text-canvas-900">
                Sign up
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

/** Small plus that doubles as the FAQ toggle (rotates 45° into a close icon). */
const PlusIconSmall = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export default Landing;
